import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { CampaignFlight } from '../../../types/Campaign';

const EST_TIMEZONE = 'America/New_York';

export type LocalCampaignFlight = Omit<CampaignFlight, 'id'> & {
    id?: number;
    tempId?: string;
    endDateTime: Date | null;
    autoAdded?: boolean;
};

interface UseCampaignFlightsProps {
    campaign: any;
    onChange: (partial: any) => void;
    setFlightValidation?: (valid: boolean) => void;
}

export function useCampaignFlights({ campaign, onChange, setFlightValidation }: UseCampaignFlightsProps) {
    const [flights, setFlights] = useState<LocalCampaignFlight[]>([]);
    const [editingFlightId, setEditingFlightId] = useState<string | null>(null);
    const tempIdCounter = useRef(0);
    const [isInitialized, setIsInitialized] = useState(false);

    // Stable refs — never reactive deps, always up-to-date
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const campaignFlightsRef = useRef(campaign.flights);
    campaignFlightsRef.current = campaign.flights;

    // Init reads from ref so campaign.flights is NOT a reactive dep.
    // This is safe because CampaignWizard gates rendering behind isLoaded,
    // guaranteeing campaign.flights is populated before this component mounts.
    // No sync useEffect means onChange never triggers a re-init loop.
    useEffect(() => {
        if (isInitialized) return;

        const currentFlights = campaignFlightsRef.current;

        if (currentFlights && currentFlights.length > 0) {
            const loaded: LocalCampaignFlight[] = currentFlights.map((f: CampaignFlight) => ({
                ...f,
                tempId: undefined,
            }));

            // Check if all flights are completed (all endDateTime in the past)
            const now = moment.tz('America/New_York');
            const allCompleted = loaded.every((f: LocalCampaignFlight) => {
                if (!f.endDateTime) return false;
                return moment.tz(f.endDateTime, 'America/New_York').isBefore(now);
            });

            if (allCompleted) {
                // Auto-add a new flight since all existing flights are completed
                // and won't be visible in the flights table
                const latestEndDate = loaded.reduce<moment.Moment | null>((latest, f) => {
                    if (!f.endDateTime) return latest;
                    const end = moment.tz(f.endDateTime, 'America/New_York');
                    return !latest || end.isAfter(latest) ? end : latest;
                }, null);

                const nowLocal = moment.tz(EST_TIMEZONE);
                let startDate: Date;
                if (latestEndDate) {
                    const dayAfterLastFlight = latestEndDate.clone().add(1, 'day');
                    // Start date cannot be in the past; start at 12:00 AM ET
                    startDate = dayAfterLastFlight.isBefore(nowLocal, 'day')
                        ? nowLocal.startOf('day').toDate()
                        : dayAfterLastFlight.startOf('day').toDate();
                } else {
                    startDate = nowLocal.startOf('day').toDate();
                }

                const endDate = moment
                    .tz(startDate, EST_TIMEZONE)
                    .endOf('month')
                    .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
                    .toDate();

                const newFlight: LocalCampaignFlight = {
                    tempId: `temp-${tempIdCounter.current++}`,
                    flightId: '',
                    startDateTime: startDate,
                    endDateTime: endDate,
                    budgetAmount: 0,
                    totalImpressions: 0,
                    totalCost: 0,
                    dspCost: 0,
                    dspBudget: 0,
                    autoAdded: true,
                };

                setFlights([newFlight, ...loaded]);
                setEditingFlightId(newFlight.tempId!);
            } else {
                setFlights(loaded);
                setEditingFlightId(null);
            }
        } else {
            const start = moment.tz(EST_TIMEZONE).set({ second: 0, millisecond: 0 });
            const initialFlight: LocalCampaignFlight = {
                tempId: `temp-${tempIdCounter.current++}`,
                flightId: '',
                startDateTime: start.toDate(),
                endDateTime: start
                    .clone()
                    .endOf('month')
                    .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
                    .toDate(),
                budgetAmount: 0,
                impressionGoal: null,
                totalImpressions: 0,
                totalCost: 0,
                dspCost: 0,
                dspBudget: 0,
            };
            setFlights([initialFlight]);
            setEditingFlightId(initialFlight.tempId!);
        }

        setIsInitialized(true);
    }, [isInitialized]);

    // Reset when campaign.id changes (switching campaigns)
    // Also resets when campaign.id first arrives (modify flow: undefined → id)
    // so init re-runs after fetchCampaign populates the real flights
    const prevCampaignIdRef = useRef(campaign.id);
    useEffect(() => {
        if (prevCampaignIdRef.current !== campaign.id) {
            prevCampaignIdRef.current = campaign.id;
            setIsInitialized(false);
        }
    }, [campaign.id]);

    const isFlightLive = useCallback((flight: LocalCampaignFlight): boolean => {
        if (!flight.startDateTime || !flight.endDateTime) return false;
        const now = moment.tz('America/New_York');
        const start = moment.tz(flight.startDateTime, 'America/New_York');
        const end = moment.tz(flight.endDateTime, 'America/New_York');
        return !start.isAfter(now) && !end.isBefore(now);
    }, []);

    const isFlightUpcoming = useCallback((flight: LocalCampaignFlight): boolean => {
        if (!flight.startDateTime) return false;
        const now = moment.tz('America/New_York');
        const start = moment.tz(flight.startDateTime, 'America/New_York');
        return start.isAfter(now);
    }, []);

    const isFlightValid = useCallback(
        (flight: LocalCampaignFlight): boolean => {
            const live = isFlightLive(flight);
            const upcoming = isFlightUpcoming(flight);

            // If a flight has an impression goal set, validate it isn't below delivered
            const impressionGoalValid =
                flight.impressionGoal == null ||
                flight.impressionGoal <= 0 ||
                !live ||
                flight.impressionGoal >= (flight.totalImpressions ?? 0);

            if (live) {
                return (
                    flight.budgetAmount > 0 &&
                    flight.budgetAmount >= (flight.totalCost ?? 0) &&
                    flight.startDateTime !== null &&
                    flight.endDateTime !== null &&
                    impressionGoalValid
                );
            } else if (upcoming) {
                return flight.startDateTime !== null && flight.endDateTime !== null;
            }
            return true;
        },
        [isFlightLive, isFlightUpcoming]
    );

    const syncFlightsToParent = useCallback((currentFlights: LocalCampaignFlight[]) => {
        const campaignFlights = currentFlights.map(({ tempId, autoAdded, ...flight }) => {
            // For existing flights, set campaignFlightId so the backend knows
            // which CampaignFlight to update when the CCR is approved
            if (flight.id !== undefined) {
                return { ...flight, campaignFlightId: flight.id };
            }
            return flight;
        });
        onChangeRef.current({ flights: campaignFlights as CampaignFlight[] });
    }, []);

    useEffect(() => {
        if (setFlightValidation) {
            if (editingFlightId !== null) {
                setFlightValidation(false);
            } else if (flights.length === 0) {
                setFlightValidation(false);
            } else {
                setFlightValidation(flights.every((flight) => isFlightValid(flight)));
            }
        }
    }, [flights, editingFlightId, isFlightValid, setFlightValidation]);

    const startDateMinDate = useMemo(() => moment().subtract(1, 'days').toDate(), []);

    const getFlightMinStartDate = useCallback(
        (flightKey: string | number): Date => {
            const latestEnd = flights
                .filter((f) => {
                    const key = f.id !== undefined ? f.id : f.tempId;
                    return key !== flightKey && f.endDateTime != null;
                })
                .map((f) => new Date(f.endDateTime!).getTime())
                .reduce((max, t) => Math.max(max, t), 0);
            return latestEnd > 0 ? moment(latestEnd).add(1, 'day').toDate() : startDateMinDate;
        },
        [flights, startDateMinDate]
    );

    const getFlightMaxEndDate = useCallback(
        (flightKey: string | number, currentStartDateTime: Date | null): Date | undefined => {
            if (!currentStartDateTime) return undefined;
            const currentStart = new Date(currentStartDateTime).getTime();
            const nextFlightStart = flights
                .filter((f) => {
                    const key = f.id !== undefined ? f.id : f.tempId;
                    return key !== flightKey && f.startDateTime != null;
                })
                .map((f) => new Date(f.startDateTime).getTime())
                .filter((t) => t > currentStart)
                .reduce((min, t) => Math.min(min, t), Infinity);
            return nextFlightStart !== Infinity ? moment(nextFlightStart).subtract(1, 'day').toDate() : undefined;
        },
        [flights]
    );

    const handleAddFlight = useCallback((): void => {
        setFlights((currentFlights) => {
            let startDate = moment.tz(EST_TIMEZONE).startOf('day').toDate();
            let endDate = moment
                .tz(startDate, EST_TIMEZONE)
                .endOf('month')
                .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
                .toDate();

            if (currentFlights.length > 0) {
                startDate = moment
                    .tz(currentFlights[0].endDateTime, EST_TIMEZONE)
                    .add(1, 'day')
                    .startOf('day')
                    .toDate();
                endDate = moment
                    .tz(startDate, EST_TIMEZONE)
                    .endOf('month')
                    .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
                    .toDate();
            }

            const newFlight: LocalCampaignFlight = {
                tempId: `temp-${tempIdCounter.current++}`,
                flightId: '',
                startDateTime: startDate,
                endDateTime: endDate,
                budgetAmount: 0,
                impressionGoal: null,
                totalImpressions: 0,
                totalCost: 0,
                dspCost: 0,
                dspBudget: 0,
            };
            setEditingFlightId(newFlight.tempId!);
            return [newFlight, ...currentFlights];
        });
    }, []);

    const handleFlightChange = useCallback((flightKey: string | number, field: keyof CampaignFlight, value: any) => {
        setFlights((currentFlights) =>
            currentFlights.map((flight) => {
                const matchKey = flight.id !== undefined ? flight.id : flight.tempId;
                if (matchKey === flightKey) {
                    return { ...flight, [field]: value };
                }
                return flight;
            })
        );
    }, []);

    const handleFlightChangeMultiple = useCallback((flightKey: string | number, changes: Partial<CampaignFlight>) => {
        setFlights((currentFlights) =>
            currentFlights.map((flight) => {
                const matchKey = flight.id !== undefined ? flight.id : flight.tempId;
                if (matchKey === flightKey) {
                    return { ...flight, ...changes };
                }
                return flight;
            })
        );
    }, []);

    const handleSaveFlight = useCallback(
        (flightKey: string | number, setCampaignBudget: (v: string) => void) => {
            const flight = flights.find((f) => {
                const matchKey = f.id !== undefined ? f.id : f.tempId;
                return matchKey === flightKey;
            });
            if (!flight || !isFlightValid(flight)) return;
            if (isFlightLive(flight) && flight.budgetAmount > 0) {
                setCampaignBudget(flight.budgetAmount.toString());
                onChangeRef.current({ budget: flight.budgetAmount });
            }
            // Mirror flight-level impression goal to campaign level for legacy reads
            if (isFlightLive(flight) && flight.impressionGoal != null && flight.impressionGoal > 0) {
                onChangeRef.current({ impressionGoal: flight.impressionGoal });
            }
            setEditingFlightId(null);
            // Sync all flights to parent explicitly on save
            syncFlightsToParent(flights);
        },
        [flights, isFlightValid, isFlightLive, syncFlightsToParent]
    );

    const handleEditFlight = useCallback((flightKey: string | number) => {
        setEditingFlightId(flightKey.toString());
    }, []);

    const handleDeleteFlight = useCallback(
        (flightKey: string | number) => {
            setFlights((currentFlights) => {
                const updated = currentFlights.filter((flight) => {
                    const matchKey = flight.id !== undefined ? flight.id : flight.tempId;
                    return matchKey !== flightKey;
                });
                syncFlightsToParent(updated);
                return updated;
            });
            if (editingFlightId === flightKey.toString()) {
                setEditingFlightId(null);
            }
        },
        [editingFlightId, syncFlightsToParent]
    );

    return {
        flights,
        editingFlightId,
        isFlightLive,
        isFlightUpcoming,
        isFlightValid,
        getFlightMinStartDate,
        getFlightMaxEndDate,
        handleAddFlight,
        handleFlightChange,
        handleFlightChangeMultiple,
        handleSaveFlight,
        handleEditFlight,
        handleDeleteFlight,
    };
}
